import { useFloating, shift, autoUpdate, offset } from "@floating-ui/react-dom";
import Image, { StaticImageData } from "next/future/image";

import AccelImg from "assets/careers/accel.svg";

import CalvinFrenchOwenImage from "assets/careers/angels/calvin-french-owen@2x.png";
import CharleyCheeverImage from "assets/careers/angels/charley-cheever@2x.png";
import DavidCramerImage from "assets/careers/angels/david-cramer@2x.png";
import JasonWarnerImage from "assets/careers/angels/jason-warner@2x.png";
import JeffSooImage from "assets/careers/angels/jeff-soo@2x.png";
import JeffWeinsteinImage from "assets/careers/angels/jeff-weinstein@2x.png";
import JoeThomasImage from "assets/careers/angels/joe-thomas@2x.png";
import JohnnyBoufarhatImage from "assets/careers/angels/johnny-boufarhat@2x.png";
import KarriSaarinenImage from "assets/careers/angels/karri-saarinen@2x.png";
import KoenBokImage from "assets/careers/angels/koen-bok@2x.png";
import LudwigPetterssonImage from "assets/careers/angels/ludwig-pettersson@2x.png";
import ManikGuptaImage from "assets/careers/angels/manik-gupta@2x.png";
import MaxStoiberImage from "assets/careers/angels/max-stoiber@2x.png";
import MirkoNovakovicImage from "assets/careers/angels/mirko-novakovic@2x.png";
import OlufemiAwomosuImage from "assets/careers/angels/olufemi-awomosu@2x.png";
import OttKaukverImage from "assets/careers/angels/ott-kaukver@2x.png";
import SteveMorinImage from "assets/careers/angels/steve-morin@2x.png";
import TanguyLeStradicImage from "assets/careers/angels/tanguy-le-stradic@2x.png";
import ZachHolmanImage from "assets/careers/angels/zach-holman@2x.png";

import CoatueImg from "assets/careers/coatue.svg";
import ChapterOneImg from "assets/careers/chapter-one@2x.png";
import YCombinatorImg from "assets/careers/ycombinator.png";
import YCombinatorHoverImg from "assets/careers/ycombinator-hover.png";

import SectionHeader from "./SectionHeader";

import styles from "./Investors.module.css";

type Angel = {
  name: string;
  title: string;
  image: StaticImageData;
};

const angels: Angel[] = [
  { name: "Jeff Soo", title: "Product Design Manager at Meta. Ex-Product Designer at Oculus VR.", image: JeffSooImage },
  { name: "Joe Thomas", title: "Co-Founder and CEO at Loom.", image: JoeThomasImage },
  { name: "Jeff Weinstein", title: "Product lead at Stripe. Previously founder of Wagon.", image: JeffWeinsteinImage },
  { name: "Johnny Boufarhat", title: "Founder and CEO at Hopin.", image: JohnnyBoufarhatImage },
  { name: "David Cramer", title: "Co-Founder and CTO at Sentry.", image: DavidCramerImage },
  { name: "Jason Warner", title: "Ex-CTO at Github, and Ex-VP of Engineering at Heroku.", image: JasonWarnerImage },
  { name: "Koen Bok", title: "CEO and Co-Founder at Framer.", image: KoenBokImage },
  {
    name: "Max Stoiber",
    title: "Co-Founder at Spectrum.chat. Founder at GraphCDN. Ex-Software Engineer at GitHub and Gatsby.",
    image: MaxStoiberImage,
  },
  { name: "Steve Morin", title: "Head of Mobile Engineering at Asana.", image: SteveMorinImage },
  { name: "Mirko Novakovic", title: "Ex-CEO at codecentric AG, and Ex-CEO at Instana.", image: MirkoNovakovicImage },
  { name: "Manik Gupta", title: "Corporate VP at Microsoft. Ex-CPO at Uber.", image: ManikGuptaImage },
  {
    name: "Femi Awomosu",
    title: "Ex-Software Engineer at Babylon Health, and Ex-Product Manager at GitHub and Shazam.",
    image: OlufemiAwomosuImage,
  },
  { name: "Tanguy le Stradic", title: "Ex-Senior Product Manager at Qonto.", image: TanguyLeStradicImage },
  {
    name: "Charley Cheever",
    title: "Founder of Expo and Quora. Ex-Engineering Manager at Facebook.",
    image: CharleyCheeverImage,
  },
  { name: "Ott Kaukver", title: "CTO at Checkout.com.", image: OttKaukverImage },
  { name: "Karri Saarinen", title: "Co-Founder and CEO at Linear. Ex-Designer at Airbnb.", image: KarriSaarinenImage },
  { name: "Zach Holman", title: "Former engineer at GitHub.", image: ZachHolmanImage },
  { name: "Calvin French-Owen", title: "Co-Founder at Segment.", image: CalvinFrenchOwenImage },
  {
    name: "Ludwig Pettersson",
    title: "Founder at Quill. Ex-Creative Director at Stripe.",
    image: LudwigPetterssonImage,
  },
];

const AngelInvestor = ({ name, title, image }: Angel) => {
  const { x, y, reference, floating, strategy } = useFloating({
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [shift(), offset(10)],
  });

  return (
    <div className={styles.angel} key={name} ref={reference}>
      <div className={styles.angelInfo} ref={floating} style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}>
        <strong className={styles.angelName}>{name}</strong>
        <span className={styles.angelTitle}>{title}</span>
      </div>
      <div className={styles.angelImageWrapper}>
        <div className={styles.angelBorder}></div>
        <Image src={image} alt={name} width={60} />
      </div>
    </div>
  );
};

const Investors = () => (
  <div className={styles.investors}>
    <SectionHeader title="Investors" anchor="investors">
      Meet the great investors and people helping to make Raycast grow.{" "}
    </SectionHeader>

    <div className={styles.funds}>
      <a href="https://www.accel.com" title="Accel" target="_blank" className={styles.investorLink} rel="noreferrer">
        <AccelImg />
      </a>
      <a href="https://www.coatue.com" title="Coatue" target="_blank" className={styles.investorLink} rel="noreferrer">
        <CoatueImg />
      </a>
      <a
        href="https://www.ycombinator.com"
        title="YCombinator"
        target="_blank"
        className={styles.ycombinatorLink}
        rel="noreferrer"
      >
        <Image src={YCombinatorImg} alt="YCombinator" width={48} className={styles.ycombinatorLinkNotHover} priority />
        <Image
          src={YCombinatorHoverImg}
          alt="YCombinator"
          width={48}
          className={styles.ycombinatorLinkHover}
          priority
        />
      </a>
      <a
        href="https://chapterone.com"
        title="Chapter One"
        target="_blank"
        className={styles.investorLink}
        rel="noreferrer"
      >
        <Image src={ChapterOneImg} alt="Chapter One" width={209} priority />
      </a>
    </div>

    <div className={styles.angelsWrapper}>
      {angels.map((angel) => (
        <AngelInvestor {...angel} key={angel.name} />
      ))}
    </div>
  </div>
);

export default Investors;
